import { Loader2, Sparkles, X, type LucideIcon } from "lucide-react"
import {
  type MouseEvent,
  type ReactNode,
  type Ref,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Button, type ButtonProps } from "@/components/ui/primitives"
import type { GenerationPriceView } from "@/hooks/use-generation-price"
import {
  DEFAULT_CONFIRM_DELAY_SECONDS,
  CONFIRM_DELAY_ARMED_BUTTON_CLASS,
  CONFIRM_DELAY_ARMED_TRAILING_WRAP_CLASS,
  useConfirmDelayAction,
} from "@/hooks/use-confirm-delay-action"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"

export function ModelCaption({ label, className }: { label: string; className?: string }) {
  return (
    <span
      className={cn("max-w-full truncate text-center text-[10px] leading-tight text-ui-text-muted", className)}
      title={label}
    >
      {label}
    </span>
  )
}

export type GenerateWithModelLabelHandle = {
  request: () => void
  /** Fire without countdown (hotkeys). Click path still uses `request`. */
  fireNow: () => void
}

type GenerateWithModelLabelProps = {
  label: string
  busyLabel: string
  modelLabel?: string | null
  /** Черновик цены: loading — спиннер в слоте, ready — сумма, hidden — слота нет. */
  cost?: GenerationPriceView | null
  /** caption — после названия модели через «·»; tooltip — когда строка под кнопкой не встаёт. iconOnly сам уходит в tooltip. */
  costPlacement?: "caption" | "tooltip"
  busy?: boolean
  disabled?: boolean
  disabledReason?: string
  onClick: () => void | boolean | Promise<void | boolean>
  variant?: ButtonProps["variant"]
  size?: ButtonProps["size"]
  stopPropagation?: boolean
  className?: string
  buttonClassName?: string
  /** Applied to visible label / busy / cancel text (not icons). */
  labelClassName?: string
  modelCaptionClassName?: string
  /** Optional control glued to the left of the generate button. */
  leading?: ReactNode
  /** Icon inside the button, immediately left of the sparkles. Idle only. */
  leadingIcon?: LucideIcon
  /** Optional control glued to the right of the generate button (e.g. duration split). */
  trailing?: ReactNode
  /**
   * Compact control: idle = icon only; armed = short countdown (no «Отменить»);
   * busy = spinner only. Full phrases stay on title/aria. No model caption.
   */
  iconOnly?: boolean
  /**
   * When the labeled row (action text, trailing controls, model caption) is wider
   * than the container, switch to iconOnly. Trailing controls stay. Measure is
   * independent of the collapsed render so the row does not expand again.
   */
  collapseToIcon?: boolean
  /**
   * Stable SPA reconnect id (`confirmDelayKey(surface, entityId, action)`).
   * Same key for generate/regenerate of one action; required even when `confirmDelaySeconds={0}`.
   */
  confirmDelayKey: string
  /** Seconds before `onClick`. Default 2; `0` = fire immediately. */
  confirmDelaySeconds?: number
  /** Countdown button label. Default: common.cancelCountdown. */
  cancelCountdownLabel?: (seconds: number) => string
  /** Gate before starting countdown. Return false to abort. */
  onBeforeArm?: () => boolean | Promise<boolean>
  /** Imperative `request` / `fireNow` for hotkeys (e.g. Cmd/Enter on canvas). */
  actionRef?: Ref<GenerateWithModelLabelHandle | null>
}

export function GenerateWithModelLabel({
  label,
  busyLabel,
  modelLabel,
  cost = null,
  costPlacement,
  busy = false,
  disabled = false,
  disabledReason,
  onClick,
  variant = "default",
  size = "sm",
  stopPropagation = false,
  className,
  buttonClassName,
  labelClassName,
  modelCaptionClassName,
  leading,
  leadingIcon: LeadingIcon,
  trailing,
  iconOnly = false,
  collapseToIcon = false,
  confirmDelayKey,
  confirmDelaySeconds = DEFAULT_CONFIRM_DELAY_SECONDS,
  cancelCountdownLabel,
  onBeforeArm,
  actionRef,
}: GenerateWithModelLabelProps) {
  const { t } = useTranslation(["common"])
  const { countdownLeft, isArmed, isPending, request, fireNow } = useConfirmDelayAction({
    persistKey: confirmDelayKey,
    seconds: confirmDelaySeconds,
    disabled: busy || disabled,
    onFire: onClick,
    onBeforeArm,
  })

  useImperativeHandle(
    actionRef,
    () => ({
      request,
      fireNow: () => {
        if (busy || disabled || isPending) return
        fireNow()
      },
    }),
    [request, fireNow, busy, disabled, isPending],
  )

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) e.stopPropagation()
    request()
  }

  const showBusy = busy || isPending
  const collapseContainerRef = useRef<HTMLDivElement>(null)
  const measureColRef = useRef<HTMLDivElement>(null)
  const measureButtonRef = useRef<HTMLButtonElement>(null)
  const leadingMeasureRef = useRef<HTMLDivElement>(null)
  const trailingMeasureRef = useRef<HTMLDivElement>(null)
  const [collapsedToIcon, setCollapsedToIcon] = useState(false)
  const [measureParent, setMeasureParent] = useState<Element | null>(null)
  // Portal moves the node between the preview and the footer without a remount.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- parent identity is read every commit
  useLayoutEffect(() => {
    const parent = collapseContainerRef.current?.parentElement ?? null
    setMeasureParent((current) => (current === parent ? current : parent))
  })
  const compactIconOnly = iconOnly || collapsedToIcon
  const compactNarrow = Boolean(labelClassName)
  // labelClassName="hidden" → short countdown at all breakpoints (sketch); "hidden sm:inline" → mobile-only (canvas).
  const alwaysCompact = labelClassName === "hidden"
  const cancelLabel =
    isArmed && countdownLeft != null
      ? (cancelCountdownLabel?.(countdownLeft) ?? t("cancelCountdown", { seconds: countdownLeft }))
      : null
  const cancelShort = isArmed && countdownLeft != null ? t("cancelCountdownShort", { seconds: countdownLeft }) : null
  const actionLabel = showBusy ? busyLabel : isArmed ? (cancelLabel ?? label) : label
  const iconClass = size === "sm" ? "h-3 w-3" : "h-4 w-4"
  const priceHint = t("generationPriceHint")
  const visibleCost = cost != null && cost.status !== "hidden" ? cost : null
  const captionCostLabel = visibleCost?.status === "ready" ? (visibleCost.label ?? "") : ""
  useLayoutEffect(() => {
    if (!collapseToIcon) {
      setCollapsedToIcon(false)
      return
    }
    const container = collapseContainerRef.current
    const column = measureColRef.current
    const measureButton = measureButtonRef.current
    if (!container || !column || !measureButton) return

    const sync = () => {
      const available = container.parentElement?.clientWidth ?? container.clientWidth
      const leadingWidth = leadingMeasureRef.current?.offsetWidth ?? 0
      const trailingWidth = trailingMeasureRef.current?.offsetWidth ?? 0
      const row = leadingWidth + measureButton.offsetWidth + trailingWidth
      const needed = Math.max(column.offsetWidth, row)
      setCollapsedToIcon(available > 0 && needed > available + 1)
    }
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(container)
    if (container.parentElement) observer.observe(container.parentElement)
    observer.observe(column)
    observer.observe(measureButton)
    const leadingNode = leadingMeasureRef.current
    const trailingNode = trailingMeasureRef.current
    if (leadingNode) observer.observe(leadingNode)
    if (trailingNode) observer.observe(trailingNode)
    return () => observer.disconnect()
  }, [collapseToIcon, label, modelLabel, captionCostLabel, size, measureParent])
  const captionDot = (
    <span aria-hidden className="shrink-0 text-[10px] leading-tight text-ui-text-faint">
      ·
    </span>
  )
  const costInTooltip =
    visibleCost != null && (costPlacement === "tooltip" || (costPlacement == null && compactIconOnly))
  const costInCaption = visibleCost != null && !costInTooltip
  const includeActionInPriceTooltip = compactIconOnly || alwaysCompact

  // iconOnly: never render long button text (label / busy / «Отменить»).
  const renderLabel = (text: string) => (compactIconOnly ? null : <span className={labelClassName}> {text}</span>)

  const priceTooltip =
    costInTooltip && visibleCost ? (
      <span className="flex flex-col items-center gap-0.5">
        {includeActionInPriceTooltip ? <span>{actionLabel}</span> : null}
        {visibleCost.status === "loading" ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-label={priceHint} />
        ) : (
          <span className="tabular-nums">{visibleCost.label}</span>
        )}
      </span>
    ) : null

  const button = (
    <Button
      type="button"
      size={size}
      variant={isArmed ? "outline" : variant}
      className={cn(
        leading ? "rounded-l-none" : null,
        trailing ? "rounded-r-none" : null,
        isArmed && CONFIRM_DELAY_ARMED_BUTTON_CLASS,
        isArmed && alwaysCompact && !compactIconOnly && "w-auto min-w-10 px-2",
        isArmed && compactNarrow && !alwaysCompact && "max-sm:w-auto max-sm:min-w-10 max-sm:px-2",
        buttonClassName,
        // After buttonClassName so iconOnly sizing wins over call-site h-8 w-8.
        compactIconOnly && !isArmed && "h-8 w-8 p-0",
        compactIconOnly && isArmed && "h-8 w-auto min-w-8 px-1.5",
      )}
      disabled={showBusy || (disabled && !isArmed)}
      title={
        costInTooltip
          ? disabled
            ? disabledReason
            : undefined
          : disabled
            ? disabledReason
            : compactIconOnly || labelClassName
              ? actionLabel
              : undefined
      }
      aria-label={compactIconOnly || isArmed || labelClassName ? actionLabel : undefined}
      onClick={handleClick}
    >
      {showBusy ? (
        <>
          <Loader2 className={cn(iconClass, "animate-spin")} />
          {renderLabel(busyLabel)}
        </>
      ) : isArmed ? (
        compactIconOnly ? (
          cancelShort ? (
            <span className="tabular-nums">{cancelShort}</span>
          ) : null
        ) : (
          <>
            <X
              className={cn(iconClass, alwaysCompact && "hidden", compactNarrow && !alwaysCompact && "max-sm:hidden")}
            />
            {compactNarrow && cancelShort ? (
              <span className={cn("tabular-nums", !alwaysCompact && "sm:hidden")}>{cancelShort}</span>
            ) : null}
            {renderLabel(cancelLabel ?? label)}
          </>
        )
      ) : (
        <>
          {LeadingIcon ? <LeadingIcon className={iconClass} aria-hidden /> : null}
          <Sparkles className={iconClass} />
          {renderLabel(label)}
        </>
      )}
    </Button>
  )

  const control = (
    <div className={cn("inline-flex flex-col items-center gap-1", !collapseToIcon && className)}>
      <div className={cn("inline-flex items-stretch", isArmed && trailing && CONFIRM_DELAY_ARMED_TRAILING_WRAP_CLASS)}>
        {collapseToIcon && leading ? (
          <div ref={leadingMeasureRef} className="inline-flex items-stretch">
            {leading}
          </div>
        ) : (
          leading
        )}
        {priceTooltip ? (
          <ActionHintTooltip label={priceTooltip} side="top">
            {button}
          </ActionHintTooltip>
        ) : (
          button
        )}
        {collapseToIcon && trailing ? (
          <div ref={trailingMeasureRef} className="inline-flex items-stretch">
            {trailing}
          </div>
        ) : (
          trailing
        )}
      </div>
      {modelLabel && !compactIconOnly ? (
        <span className="inline-flex max-w-full items-center justify-center gap-1">
          <ModelCaption label={modelLabel} className={cn("min-w-0", modelCaptionClassName)} />
          {costInCaption && visibleCost?.status === "loading" ? (
            <>
              {captionDot}
              <Loader2 className="h-2.5 w-2.5 shrink-0 animate-spin text-ui-text-muted" aria-label={priceHint} />
            </>
          ) : null}
          {costInCaption && visibleCost?.status === "ready" && visibleCost.label ? (
            <>
              {captionDot}
              <ModelCaption label={visibleCost.label} className={cn("shrink-0 tabular-nums", modelCaptionClassName)} />
            </>
          ) : null}
        </span>
      ) : costInCaption && visibleCost?.status === "ready" && visibleCost.label ? (
        <ModelCaption label={visibleCost.label} className={cn("tabular-nums", modelCaptionClassName)} />
      ) : null}
    </div>
  )

  if (!collapseToIcon) return control

  return (
    <div ref={collapseContainerRef} className={cn("inline-flex max-w-full min-w-0 flex-col items-center", className)}>
      <div
        ref={measureColRef}
        className="pointer-events-none fixed top-0 left-0 -z-50 inline-flex flex-col items-center gap-1 opacity-0"
        aria-hidden
      >
        <Button
          ref={measureButtonRef}
          type="button"
          tabIndex={-1}
          size={size}
          variant={variant}
          className={cn(leading ? "rounded-l-none" : null, trailing ? "rounded-r-none" : null)}
        >
          {LeadingIcon ? <LeadingIcon className={iconClass} aria-hidden /> : null}
          <Sparkles className={iconClass} />
          <span> {label}</span>
        </Button>
        {modelLabel || captionCostLabel ? (
          <span className="inline-flex items-center gap-1 text-[10px] leading-tight whitespace-nowrap">
            {modelLabel ? <span>{modelLabel}</span> : null}
            {captionCostLabel ? (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">{captionCostLabel}</span>
              </>
            ) : null}
          </span>
        ) : null}
      </div>
      {control}
    </div>
  )
}
